import { useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { pickListsApi } from '@/api/pickListsApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * Forwards an old `/inventory/kits/builds/:id` link to the kit run itself.
 *
 * Kits are no longer assembled on a page of their own — a kit run is worked
 * through its procedure, like any other. This exists only so bookmarked and
 * emailed build links keep working for a release: the router's catch-all sends
 * unknown paths to the dashboard without a word, which is indistinguishable
 * from the kit having been deleted.
 */
export default function KitBuildRedirect() {
  const { id } = useParams<{ id: string }>()
  const { selectedTenantId: tenantId } = useTenant()
  const [procedureId, setProcedureId] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!id || !tenantId) return
    let cancelled = false
    pickListsApi
      .getKitBuild(tenantId, id)
      .then(b => !cancelled && setProcedureId(b.procedureId))
      .catch(() => !cancelled && setFailed(true))
    return () => {
      cancelled = true
    }
  }, [id, tenantId])

  if (procedureId) return <Navigate to={`/kits/runs/${procedureId}`} replace />
  // The build is gone or not ours: the kit list is the honest destination, and
  // it is one the user can act on.
  if (failed) return <Navigate to='/kits' replace />

  return (
    <div className='flex items-center gap-2 p-8 text-sm text-muted-foreground'>
      <Loader2 className='h-4 w-4 animate-spin' />
      Opening the kit run…
    </div>
  )
}
